(function () {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');
  const TAU = Math.PI * 2;

  /* ------------------------------------------------------------------ *
   * Waveform: sum of sines + cosines for a believable chant signal.
   * Returns a fresh SVG path "d" string each call.
   * ------------------------------------------------------------------ */
  function buildWavePath(opts) {
    const points = opts.points || 80;
    const w = opts.width || 200;
    const cy = opts.cy || 30;
    const amp = opts.amplitude || 12;
    const t = opts.time || 0;
    const harmonics = opts.harmonics;

    let d = '';
    for (let i = 0; i <= points; i++) {
      const x = i / points;
      let y = 0;
      for (let h = 0; h < harmonics.length; h++) {
        const har = harmonics[h];
        y += har.amp * har.fn(har.freq * x * TAU + har.phase + t * har.speed);
      }
      const px = (x * w).toFixed(2);
      const py = (cy + y * amp).toFixed(2);
      d += (i === 0 ? 'M ' : ' L ') + px + ' ' + py;
    }
    return d;
  }

  class ChakrikkaHowItWorks extends HTMLElement {
    connectedCallback() {
      this.refreshRefs();

      this.index = 0;
      this.progress = 0;
      this.paused = false;
      this.offscreen = false;
      this.userPaused = false;
      this.rafId = null;
      this.lastTs = 0;
      this.startTs = 0;
      this.careManualIndex = -1;

      this._onTileClick = this.onTileClick.bind(this);
      this._onTabClick = this.onTabClick.bind(this);
      this._onTick = this.tick.bind(this);
      this._onMotionChange = () => this.applyMotionPreference();
      this._onBlockSelect = this.onBlockSelect.bind(this);
      this._onSectionLoad = this.onSectionLoad.bind(this);

      this.bindTiles();
      this.bindCareTabs();
      this.prepareWavePanels();

      if (REDUCED_MOTION.addEventListener) {
        REDUCED_MOTION.addEventListener('change', this._onMotionChange);
      } else if (REDUCED_MOTION.addListener) {
        REDUCED_MOTION.addListener(this._onMotionChange);
      }

      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:section:load', this._onSectionLoad);

      this.setupIntersectionObserver();
      this.setActive(0, false);
      this.resume();
    }

    disconnectedCallback() {
      this.stopLoop();
      this.steps.forEach((step) => step.removeEventListener('click', this._onTileClick));
      this.careTabs.forEach((tab) => tab.removeEventListener('click', this._onTabClick));
      if (REDUCED_MOTION.removeEventListener) {
        REDUCED_MOTION.removeEventListener('change', this._onMotionChange);
      } else if (REDUCED_MOTION.removeListener) {
        REDUCED_MOTION.removeListener(this._onMotionChange);
      }
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      if (this._io) this._io.disconnect();
    }

    refreshRefs() {
      this.steps = Array.from(this.querySelectorAll('.hiw-step'));
      this.panels = Array.from(this.querySelectorAll('.hiw-panel'));
      this.carousel = this.querySelector('.chakrikka-how-it-works__steps');
      this.careTabs = Array.from(this.querySelectorAll('.hiw-tab'));
      this.cycle = parseInt(this.dataset.cycle, 10) || 10000;
      this.autoplay = this.dataset.autoplay !== 'false';
    }

    get reduced() {
      return REDUCED_MOTION.matches;
    }

    get count() {
      return this.steps.length;
    }

    bindTiles() {
      this.steps.forEach((step, i) => {
        if (!step.dataset.index) step.dataset.index = String(i);
        step.addEventListener('click', this._onTileClick);
      });
    }

    bindCareTabs() {
      this.careTabs.forEach((tab) => tab.addEventListener('click', this._onTabClick));
    }

    prepareWavePanels() {
      this.panels.forEach((panel) => {
        if (panel.dataset.panel !== 'charge_ring') return;
        const lines = panel.querySelectorAll('.hiw-wave__line');
        lines.forEach((line, idx) => {
          /* Three layered voice-like signals: a low fundamental (bass),
             a mid throat resonance, and a higher overtone (treble). */
          if (idx === 0) {
            line._harmonics = [
              { freq: 1.0, amp: 0.55, phase: 0.0, speed: 1.6, fn: Math.sin },
              { freq: 2.3, amp: 0.22, phase: 1.1, speed: 2.2, fn: Math.sin },
              { freq: 0.5, amp: 0.30, phase: 0.4, speed: 0.9, fn: Math.cos }
            ];
            line._scale = 1.0;
          } else if (idx === 1) {
            line._harmonics = [
              { freq: 1.7, amp: 0.40, phase: 0.6, speed: 2.4, fn: Math.sin },
              { freq: 3.9, amp: 0.25, phase: 2.0, speed: 3.0, fn: Math.cos },
              { freq: 1.1, amp: 0.20, phase: 1.4, speed: 1.7, fn: Math.sin }
            ];
            line._scale = 0.85;
          } else {
            line._harmonics = [
              { freq: 5.2, amp: 0.30, phase: 0.0, speed: 4.4, fn: Math.cos },
              { freq: 8.4, amp: 0.18, phase: 1.9, speed: 5.8, fn: Math.sin },
              { freq: 11.3, amp: 0.10, phase: 0.7, speed: 7.1, fn: Math.cos }
            ];
            line._scale = 0.65;
          }
        });
      });
    }

    onTileClick(event) {
      const step = event.currentTarget;
      const i = parseInt(step.dataset.index, 10) || 0;
      this.setActive(i, true);
    }

    onTabClick(event) {
      const tab = event.currentTarget;
      const panel = tab.closest('.hiw-panel');
      if (!panel) return;
      const idx = parseInt(tab.dataset.tabIndex, 10) || 0;
      this.careManualIndex = idx;
      this.applyCareIndex(panel, idx);
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) return;
      this._io = new IntersectionObserver(
        (entries) => {
          const visible = entries.some((e) => e.isIntersecting);
          this.offscreen = !visible;
          if (visible) this.resume();
          else this.pause();
        },
        { root: null, threshold: 0.2 }
      );
      this._io.observe(this);
    }

    setActive(index, userInitiated) {
      if (this.count === 0) return;
      const len = this.count;
      this.index = ((index % len) + len) % len;
      this.progress = 0;
      this.careManualIndex = -1;

      this.steps.forEach((step, i) => {
        const isActive = i === this.index;
        step.classList.toggle('is-active', isActive);
        step.setAttribute('aria-selected', isActive ? 'true' : 'false');
        const bar = step.querySelector('.hiw-step__progress-fill');
        if (bar) bar.style.transform = 'scaleX(0)';
      });

      this.panels.forEach((panel, i) => {
        const isActive = i === this.index;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.enterPanel(this.panels[this.index]);
      this.scrollActiveIntoView();

      if (userInitiated) {
        this.userPaused = false;
        this.restartLoop();
      }
    }

    scrollActiveIntoView() {
      if (!MOBILE_MQ.matches || !this.carousel) return;
      const step = this.steps[this.index];
      if (!step) return;
      const target =
        step.offsetLeft - (this.carousel.clientWidth - step.clientWidth) / 2;
      this.carousel.scrollTo({
        left: Math.max(0, target),
        behavior: this.reduced ? 'auto' : 'smooth'
      });
    }

    enterPanel(panel) {
      if (!panel) return;
      const type = panel.dataset.panel;

      if (type === 'intention_graph') {
        this.updateGraph(panel);
      }

      if (type === 'aftercare') {
        this.applyCareIndex(panel, 0);
      }

      if (type === 'charge_ring') {
        this.updateRing(panel, this.reduced ? 1 : 0);
      }

      if (type === 'seal') {
        const seal = panel.querySelector('.hiw-seal');
        if (seal) {
          seal.classList.remove('is-sealed');
          void seal.offsetWidth;
          const trigger = () => seal.classList.add('is-sealed');
          if (this.reduced) trigger();
          else window.setTimeout(trigger, 700);
        }
      }
    }

    updateGraph(panel) {
      const chips = panel.querySelectorAll('.hiw-chip');
      const n = chips.length;
      if (!n) return;
      let idx = Math.floor(this.progress * n);
      if (idx >= n) idx = n - 1;
      const bead = String(idx);
      chips.forEach((chip, i) => chip.classList.toggle('is-selected', i === idx));
      panel
        .querySelectorAll('.hiw-graph__link')
        .forEach((link) => link.classList.toggle('is-active', link.dataset.bead === bead));
      panel
        .querySelectorAll('.hiw-bead')
        .forEach((b) => b.classList.toggle('is-lit', b.dataset.bead === bead));
    }

    updateCareAuto(panel) {
      const tabs = panel.querySelectorAll('.hiw-tab');
      const n = tabs.length;
      if (!n) return;
      let autoIdx = Math.floor(this.progress * n);
      if (autoIdx >= n) autoIdx = n - 1;

      /* Per-tab progress fill: each tab gets a 1/n slice of the cycle. */
      tabs.forEach((tab, i) => {
        const fill = tab.querySelector('.hiw-tab__progress-fill');
        if (!fill) return;
        let local = 0;
        if (i < autoIdx) local = 1;
        else if (i === autoIdx) local = (this.progress * n) - autoIdx;
        fill.style.transform = 'scaleX(' + local.toFixed(4) + ')';
      });

      const idx = this.careManualIndex >= 0 ? this.careManualIndex : autoIdx;
      this.applyCareIndex(panel, idx);
    }

    applyCareIndex(panel, idx) {
      const tabs = panel.querySelectorAll('.hiw-tab');
      const items = panel.querySelectorAll('.hiw-tabpanel');
      tabs.forEach((tab, i) => {
        const on = i === idx;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      items.forEach((item, i) => {
        const on = i === idx;
        item.classList.toggle('is-active', on);
        item.toggleAttribute('hidden', !on);
      });
    }

    updateProgressVisual(ts) {
      const step = this.steps[this.index];
      if (step) {
        const bar = step.querySelector('.hiw-step__progress-fill');
        if (bar) bar.style.transform = 'scaleX(' + this.progress.toFixed(4) + ')';
      }
      const panel = this.panels[this.index];
      if (!panel) return;
      switch (panel.dataset.panel) {
        case 'charge_ring':
          this.updateRing(panel, this.progress);
          this.updateWave(panel, ts);
          break;
        case 'intention_graph':
          this.updateGraph(panel);
          break;
        case 'aftercare':
          this.updateCareAuto(panel);
          break;
      }
    }

    updateRing(panel, fraction) {
      const f = Math.min(1, Math.max(0, fraction));
      const ring = panel.querySelector('.hiw-ring');
      if (!ring) return;
      const arc = panel.querySelector('.hiw-ring__progress');
      if (arc) {
        const len = arc.getTotalLength ? arc.getTotalLength() : 540;
        arc.style.strokeDasharray = String(len);
        arc.style.strokeDashoffset = String(len * (1 - f));
      }
      const days = parseInt(panel.dataset.days, 10) || 21;
      const current = Math.round(f * days);
      const dayEl = panel.querySelector('.hiw-ring__day-value');
      if (dayEl && dayEl.textContent !== String(current)) {
        dayEl.textContent = String(current);
        dayEl.classList.remove('is-rolling');
        void dayEl.offsetWidth;
        dayEl.classList.add('is-rolling');
      }
      ring.style.setProperty('--hiw-charge', f.toFixed(4));
      panel.querySelectorAll('.hiw-ring__tick').forEach((tick, i) => {
        tick.classList.toggle('is-filled', i < current);
      });
    }

    updateWave(panel, ts) {
      const lines = panel.querySelectorAll('.hiw-wave__line');
      if (!lines.length) return;
      const t = (ts || performance.now()) / 1000;
      /* Charge ramps amplitude from a quiet base to full chant volume. */
      const charge = this.progress;
      const baseAmp = 8 + 14 * charge;
      lines.forEach((line) => {
        if (!line._harmonics) return;
        const d = buildWavePath({
          width: 200,
          cy: 30,
          amplitude: baseAmp * line._scale,
          time: t,
          harmonics: line._harmonics
        });
        line.setAttribute('d', d);
      });
    }

    tick(ts) {
      if (!this.lastTs) {
        this.lastTs = ts;
        this.startTs = ts;
      }
      const dt = ts - this.lastTs;
      this.lastTs = ts;

      this.progress += dt / this.cycle;

      if (this.progress >= 1) {
        this.progress = 1;
        this.updateProgressVisual(ts);
        this.lastTs = 0;
        this.setActive(this.index + 1, false);
        if (this.isRunnable()) {
          this.rafId = window.requestAnimationFrame(this._onTick);
        }
        return;
      }

      this.updateProgressVisual(ts);
      this.rafId = window.requestAnimationFrame(this._onTick);
    }

    isRunnable() {
      return (
        this.autoplay &&
        !this.reduced &&
        !this.paused &&
        !this.offscreen &&
        !this.userPaused &&
        this.count > 1
      );
    }

    startLoop() {
      if (!this.isRunnable() || this.rafId != null) return;
      this.lastTs = 0;
      this.rafId = window.requestAnimationFrame(this._onTick);
    }

    stopLoop() {
      if (this.rafId != null) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      this.lastTs = 0;
    }

    restartLoop() {
      this.stopLoop();
      this.startLoop();
    }

    pause() {
      this.paused = true;
      this.stopLoop();
    }

    resume() {
      this.paused = false;
      this.startLoop();
    }

    applyMotionPreference() {
      if (this.reduced) {
        this.stopLoop();
        const panel = this.panels[this.index];
        if (panel && panel.dataset.panel === 'charge_ring') this.updateRing(panel, 1);
      } else {
        this.resume();
      }
    }

    onBlockSelect(event) {
      const sid = event.detail && event.detail.sectionId;
      if (!sid || String(sid) !== String(this.dataset.sectionId)) return;
      const blockId = event.detail.blockId;
      const step = this.querySelector('.hiw-step[data-block-id="' + blockId + '"]');
      if (!step) return;
      const i = parseInt(step.dataset.index, 10) || 0;
      this.userPaused = true;
      this.pause();
      this.setActive(i, false);
    }

    onSectionLoad(event) {
      const sid = event.detail && event.detail.sectionId;
      if (!sid || String(sid) !== String(this.dataset.sectionId)) return;
      this.stopLoop();
      this.refreshRefs();
      this.bindTiles();
      this.bindCareTabs();
      this.prepareWavePanels();
      this.setActive(0, false);
      this.resume();
    }
  }

  if (!customElements.get('chakrikka-how-it-works')) {
    customElements.define('chakrikka-how-it-works', ChakrikkaHowItWorks);
  }
})();
